import React, { useState, useRef, useEffect } from 'react';
import './SearchableSelect.css';

interface SearchableSelectOption {
  value: string;
  label: string;
  isRequired?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  maxLength?: number;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  onBlur,
  options,
  placeholder = "選択してください",
  searchPlaceholder = "検索キーワードを入力",
  className = "",
  error = false,
  disabled = false,
  maxLength = 255
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = (options || []).filter(option =>
    option && option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected option label
  const selectedOption = (options || []).find(option => option && option.value === value);
  const displayValue = selectedOption && selectedOption.label ? selectedOption.label : '';

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    if (newSearchTerm.length <= maxLength) {
      setSearchTerm(newSearchTerm);
      setFocusedIndex(-1);
    }
  };

  // Handle option selection
  const handleOptionSelect = (option: SearchableSelectOption) => {
    // Don't select if option is disabled
    if (option.disabled) {
      return;
    }
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
    setFocusedIndex(-1);
  };

  // Handle tooltip show
  const handleOptionMouseEnter = (option: SearchableSelectOption, event: React.MouseEvent) => {
    if (option.tooltip) {
      setTooltip({
        text: option.tooltip,
        x: event.clientX,
        y: event.clientY
      });
    }
  };

  // Handle mouse move for tooltip following
  const handleOptionMouseMove = (option: SearchableSelectOption, event: React.MouseEvent) => {
    if (option.tooltip && tooltip) {
      setTooltip({
        text: option.tooltip,
        x: event.clientX,
        y: event.clientY
      });
    }
  };

  // Handle tooltip hide
  const handleOptionMouseLeave = () => {
    setTooltip(null);
  };

  // Check if dropdown should open upward (near bottom of screen)
  const shouldOpenUpward = () => {
    if (!containerRef.current) return false;
    
    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // If there's less than 300px below and more space above, open upward
    return spaceBelow < 300 && spaceAbove > spaceBelow;
  };

  // Handle dropdown toggle
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
        setFocusedIndex(-1);
        // Focus search input when opening
        setTimeout(() => {
          searchInputRef.current?.focus();
          
          // Check if we need to position dropdown upward
          if (containerRef.current && shouldOpenUpward()) {
            containerRef.current.classList.add('dropdown-up');
          } else {
            containerRef.current?.classList.remove('dropdown-up');
          }
        }, 0);
      } else {
        // Remove positioning class when closing
        containerRef.current?.classList.remove('dropdown-up');
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        handleToggle();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => {
          let nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
          // Skip disabled options
          while (nextIndex !== prev && filteredOptions[nextIndex]?.disabled) {
            nextIndex = nextIndex < filteredOptions.length - 1 ? nextIndex + 1 : 0;
          }
          return nextIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => {
          let nextIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
          // Skip disabled options
          while (nextIndex !== prev && filteredOptions[nextIndex]?.disabled) {
            nextIndex = nextIndex > 0 ? nextIndex - 1 : filteredOptions.length - 1;
          }
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
    }
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        // Clean up positioning class
        containerRef.current.classList.remove('dropdown-up');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`searchable-select ${className} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <div
        className="searchable-select-trigger"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          // Only trigger onBlur if focus is moving outside the component
          if (!containerRef.current?.contains(e.relatedTarget as Node)) {
            setIsOpen(false);
            setSearchTerm('');
            setFocusedIndex(-1);
            onBlur?.();
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={!displayValue ? 'placeholder' : ''}>
          {displayValue || placeholder}
        </span>
        <span className="searchable-select-arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
              maxLength={maxLength}
              className="searchable-select-search-input"
            />
            <span className="searchable-select-search-arrow">
              ▲
            </span>
          </div>
          
          <div className="searchable-select-options" role="listbox">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-no-options">
                該当する項目がありません
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`searchable-select-option ${
                    index === focusedIndex ? 'focused' : ''
                  } ${option.value === value ? 'selected' : ''} ${
                    option.disabled ? 'disabled' : ''
                  }`}
                  onClick={() => handleOptionSelect(option)}
                  onMouseEnter={(e) => handleOptionMouseEnter(option, e)}
                  onMouseMove={(e) => handleOptionMouseMove(option, e)}
                  onMouseLeave={handleOptionMouseLeave}
                  role="option"
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled}
                >
                  {option.isRequired ? (
                    <>
                      {option.label.replace(' (必須)', '')}
                      <span className="required-indicator">(必須)</span>
                    </>
                  ) : (
                    option.label || ''
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          className="searchable-select-tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            zIndex: 10000
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
